/**
 * The activity's rubrics — several per question, all tutor-reviewed. Replaces the old catalog +
 * per-activity picker pair; the document shape is unchanged, because B8 is about storage:
 *
 *  | `data.rubrics`     | `Record<rubricId, ReviewRubric>` | this hook, via the store |
 *  | `Activity.rubrics` | `string[]` of catalog ids        | this hook, via the store |
 *
 * Both halves now move together and only from here: adding mints a catalog entry AND appends its
 * id; removing deletes the entry, which the store strips from every referencing activity. So a
 * rubric has exactly one owner — nothing can be shared between activities or left orphaned.
 *
 * A DANGLING ID (held by the activity, absent from the catalog — refinement 5) is surfaced and
 * removable, never silently dropped: dropping it would hide what validation exists to report.
 *
 * AUTO-GRADED is the same fact read the other way round. There is no `autoGraded` field, and a
 * question with no rubrics is one nothing needs a tutor to read. So the switch is derived, and
 * flipping it is the add/remove above plus `points` — an auto-graded question keeps it, a reviewed
 * one is scored by its rubrics' bands and has the key deleted. Turning the switch on therefore
 * DELETES every rubric the question carries: there is nowhere to park an unreferenced one.
 *
 * The owning ids arrive as GETTERS: the activity's screen can change under it.
 */
import { computed } from 'vue'

import type { ComputedRef } from 'vue'
import type { Activity } from './interfaces'

import { pluralize } from '@shared/lib/format'

import { useAssessmentEditorStore } from './store'

/** Removal deletes the rubric itself, not just this question's reference to it — there is no
 *  catalog left to put it back from. */
export const RUBRIC_REMOVE_HINT = 'Remove this rubric. It is deleted along with its levels and cannot be brought back from here.'

export interface ActivityRubricProps {
  screenId: string
  activityId: string
}

interface UseActivityRubricOptions {
  screenId: () => string
  activityId: () => string
}

/** One card in the activity's rubric list. */
interface ActivityRubricItem {
  id: string
  /** 1-based, as the author counts them. */
  position: number
  /** False when the id is held by the activity but absent from the catalog (refinement 5) — the
   *  card then reports the dangling id instead of rendering an editor over nothing. */
  exists: boolean
}

interface UseActivityRubricReturn {
  refRubrics: ComputedRef<ActivityRubricItem[]>
  refHasRubrics: ComputedRef<boolean>
  /** True when at least one held id has no catalog entry (refinement 5). */
  refHasMissing: ComputedRef<boolean>
  /** The sentence that reports them. Empty when none are missing. */
  refMissingNote: ComputedRef<string>
  /** No rubrics ⇒ the machine marks this question. Derived, never stored. */
  refIsAutoGraded: ComputedRef<boolean>
  /** `true` drops every rubric; `false` mints the first one and deletes `points`. */
  setAutoGraded: (value: boolean) => void
  addRubric: () => void
  removeRubric: (rubricId: string) => void
}

export const useActivityRubric = (
  { screenId, activityId }: UseActivityRubricOptions,
): UseActivityRubricReturn => {
  const store = useAssessmentEditorStore()

  const refActivity = computed<Activity | null>(() =>
    store.getScreens
      .find(screen => screen.id === screenId())
      ?.activities.find(activity => activity.id === activityId()) ?? null,
  )

  const refIds = computed<string[]>(() => refActivity.value?.rubrics ?? [])

  const refRubrics = computed<ActivityRubricItem[]>(() =>
    refIds.value.map((id, index) => ({
      id,
      position: index + 1,
      exists: store.getRubricCatalog[id] !== undefined,
    })),
  )

  const refHasRubrics = computed<boolean>(() => refIds.value.length > 0)

  const refMissingCount = computed<number>(() => refRubrics.value.filter(item => !item.exists).length)

  const refHasMissing = computed<boolean>(() => refMissingCount.value > 0)

  const refMissingNote = computed<string>(() => {
    if (!refMissingCount.value) {
      return ''
    }
    const noun = pluralize(refMissingCount.value, { one: 'rubric', other: 'rubrics' })

    return `Validation reports the missing ${noun}. Dropping a reference here changes nothing else.`
  })

  /**
   * Both halves of B8 in one act: mint the catalog entry, then APPEND its id to this activity.
   * Appending, not replacing — a question may be reviewed against several things.
   */
  const addRubric = (): void => {
    const rubricId = store.addRubric()
    if (!rubricId) {
      return
    }
    store.setActivityRubrics(screenId(), activityId(), [...refIds.value, rubricId])
  }

  /**
   * Deletes the catalog entry, which the store's `removeRubric` also strips from every
   * `activity.rubrics`. A dangling id has no entry to delete, so it is dropped from the reference
   * list directly — the one case where this hook writes the array itself.
   */
  const removeRubric = (rubricId: string): void => {
    if (!refIds.value.includes(rubricId)) {
      return
    }
    if (store.getRubricCatalog[rubricId] === undefined) {
      store.setActivityRubrics(
        screenId(),
        activityId(),
        refIds.value.filter(id => id !== rubricId),
      )
      return
    }
    store.removeRubric(rubricId)
  }

  const refIsAutoGraded = computed<boolean>(() => refIds.value.length === 0)

  /**
   * The switch. Auto-graded and tutor-reviewed are the two sides of "does this question carry any
   * rubric", so this is remove-them-all / add-the-first — plus the points key, which only one of
   * the two states can hold, or a reviewed question would be scored twice by two numbers.
   */
  const setAutoGraded = (value: boolean): void => {
    if (value === refIsAutoGraded.value) {
      return
    }
    if (value) {
      // A copy: `removeRubric` rewrites the array this loop would otherwise be walking.
      ;[...refIds.value].forEach(removeRubric)
      return
    }
    addRubric()
    store.updateActivity(screenId(), activityId(), { points: null })
  }

  return {
    refRubrics,
    refHasRubrics,
    refHasMissing,
    refMissingNote,
    refIsAutoGraded,
    addRubric,
    removeRubric,
    setAutoGraded,
  }
}
